import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUnreadCount } from '../api/notifications';
import { useVisibilityAwarePolling } from './useVisibilityAwarePolling';

const POLL_INTERVAL_MS = 30_000;
const JITTER_MS = 5_000;

// SF-122 Phase 2 C8: every mounted consumer still polls via the project's shared
// `useVisibilityAwarePolling` primitive (CLAUDE.md: "All polled badges ... must use this hook") —
// but the actual `getUnreadCount` network call and its resulting value are deduplicated across
// simultaneously-mounted consumers (Sidebar + this spec's new shell notification bell) via a
// module-level shared cache, dedup window, and broadcast, instead of each call independently
// hitting the network on its own 30s+jitter timer.
//
// SF-155 Component 6: widened from `useUnreadCount(): number` to
// `useUnreadCount(): { count: number; hasLoaded: boolean }` — `sharedHasLoaded` tracks whether the
// first poll has settled (success *or* failure) so a fresh consumer (e.g. World Hub's Recent News
// card) can distinguish "still loading" from "confirmed zero" instead of rendering a false "All
// caught up" before any real data has arrived.
let sharedCount = 0;
let sharedHasLoaded = false;
let lastFetchedAt = 0;
let inFlight: Promise<void> | null = null;
// SF-155 Component 6: bumped by `markUnreadCountRead()` — lets `sharedPoll` detect and drop a
// response from a fetch that was already in flight *before* the mark-read ack landed, which would
// otherwise resolve afterward and clobber the just-zeroed count back to a stale nonzero value.
let readEpoch = 0;
const listeners = new Set<(count: number, hasLoaded: boolean) => void>();

// Comfortably under POLL_INTERVAL_MS (30s) so two consumers' independently-jittered timers
// (spread 0-5s apart) collapse to one actual request per cycle, without suppressing a
// genuinely-later poll from the same or another consumer.
const DEDUP_WINDOW_MS = 25_000;

function notify(): void {
  listeners.forEach((listener) => listener(sharedCount, sharedHasLoaded));
}

function setSharedCount(next: number): void {
  if (next === sharedCount) return;
  sharedCount = next;
  notify();
}

async function sharedPoll(worldId: string, provinceId: string): Promise<void> {
  if (inFlight) {
    await inFlight;
    return;
  }
  if (Date.now() - lastFetchedAt < DEDUP_WINDOW_MS) {
    return;
  }

  const epochAtStart = readEpoch;
  inFlight = (async () => {
    try {
      const result = await getUnreadCount(worldId, provinceId);
      lastFetchedAt = Date.now();
      // Drop a response from a fetch issued before a mark-read ack landed while it was in
      // flight — applying it now would silently revert the just-zeroed count to a stale value.
      if (result.data != null && epochAtStart === readEpoch) {
        const raw = result.data.count;
        setSharedCount(Number.isFinite(raw) ? raw : 0);
      }
    } finally {
      // A failed first fetch is a resolved state, not a still-loading one — hasLoaded flips on
      // both the success and failure paths, so `finally` (not the try block) is the right place.
      if (!sharedHasLoaded) {
        sharedHasLoaded = true;
        notify();
      }
      inFlight = null;
    }
  })();

  await inFlight;
}

/**
 * Pushes the shared unread count to 0 and broadcasts immediately, independent of the poll cycle.
 * Called by `RecentNews.tsx` once its mark-read-on-mount request resolves, so the badge/card
 * reflects "all read" right away instead of waiting up to ~30s+jitter for the next poll tick
 * (SF-155 Component 6/7 — Recent News staleness-after-read fix). Also resolves `hasLoaded` to
 * `true` — a mark-read acknowledgement is itself proof the count is known (0), so a consumer that
 * mounts before the shared poller's own first poll has fired must not still see "Loading…".
 */
export function markUnreadCountRead(): void {
  readEpoch++;
  const changed = sharedCount !== 0 || !sharedHasLoaded;
  sharedCount = 0;
  sharedHasLoaded = true;
  if (changed) notify();
}

/**
 * Test-only: resets the shared poller's module state between test files/cases, mirroring the
 * project's `_resetKingdomRelationshipsCache()` convention for module-level cache/state reset.
 */
export function _resetUnreadCountPoller(): void {
  sharedCount = 0;
  sharedHasLoaded = false;
  lastFetchedAt = 0;
  inFlight = null;
  readEpoch = 0;
  listeners.clear();
}

export function useUnreadCount(): { count: number; hasLoaded: boolean } {
  const { worldId, provinceId } = useAuth();
  const [count, setCount] = useState<number>(sharedCount);
  const [hasLoaded, setHasLoaded] = useState<boolean>(sharedHasLoaded);

  useEffect(() => {
    const listener = (nextCount: number, nextHasLoaded: boolean) => {
      setCount(nextCount);
      setHasLoaded(nextHasLoaded);
    };
    listeners.add(listener);
    setCount(sharedCount);
    setHasLoaded(sharedHasLoaded);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const poll = useCallback(async () => {
    if (!worldId || !provinceId) return;
    await sharedPoll(worldId, provinceId);
  }, [worldId, provinceId]);

  useVisibilityAwarePolling(poll, POLL_INTERVAL_MS, JITTER_MS);

  return { count, hasLoaded };
}
